import { GapValues } from "@/lib/utils/GapValues";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { Column, Row, Text } from "@stenajs-webui/core";
import { FlatButton, PrimaryButton, stenaTrash } from "@stenajs-webui/elements";
import { Popover } from "@stenajs-webui/tooltip";
import * as React from "react";
import { useState } from "react";
import { parentStretchState } from "../lib/parentStretchState";
import { ParentHistoryEntry } from "../types";

interface Props {
  entry: ParentHistoryEntry;
  today: string;
  /** Settles once the write and the read after it are done. */
  onDelete: () => Promise<unknown>;
}

/**
 * What deleting the stretch costs, which is not the same for all of them.
 *
 * A stretch that has not started has charged nothing, so taking it out
 * loses nothing. One that has is a record of what the account was charged
 * under, and removing it says those days had no parent - which is worth
 * saying out loud before it happens.
 */
const prompt = (entry: ParentHistoryEntry, today: string): string => {
  const state = parentStretchState(entry, today);

  if (state === "FUTURE") {
    return `Delete #${entry.parentCustomerNo}, due to start ${entry.validFrom}? It has not priced anything yet, so nothing is kept.`;
  }
  if (state === "ACTIVE") {
    return `Delete #${entry.parentCustomerNo}? It is the parent now, so the account is left with none from ${entry.validFrom} onwards.`;
  }

  return `Delete #${entry.parentCustomerNo}? The account will read as having had no parent from ${entry.validFrom} to ${entry.validTo}.`;
};

export const DeleteParentButton: React.FC<Props> = ({
  entry,
  today,
  onDelete,
}) => {
  /** Kept outside the popover's render so a delete survives a re-render. */
  const [deleting, setDeleting] = useState(false);

  return (
    <Popover
      zIndex={ZIndex.highest}
      appendTo={document.body}
      trigger={"click"}
      renderTrigger={(props) => (
        <FlatButton
          {...props}
          leftIcon={stenaTrash}
          variant={"danger"}
          aria-label={`Delete parent #${entry.parentCustomerNo} from ${entry.validFrom}`}
        />
      )}
    >
      {({ onRequestClose }) => (
        /*
         * Laid out here rather than with ActionPrompt, which puts a single
         * space between the text and the buttons and sets no width - too
         * tight for a sentence this long. Matches the date popover instead.
         */
        <Column gap={GapValues.SIXTEEN} width={"340px"}>
          <Text>{prompt(entry, today)}</Text>

          <Row gap={GapValues.SIXTEEN} justifyContent={"flex-end"}>
            <FlatButton
              label={"Cancel"}
              disabled={deleting}
              onClick={onRequestClose}
            />
            {/*
            The popover stays open for the write, so the spinner has
            somewhere to sit, and shuts once the row is already gone from
            the table behind it.
          */}
            <PrimaryButton
              label={"Delete"}
              variant={"danger"}
              loading={deleting}
              loadingLabel={"Deleting"}
              onClick={async () => {
                setDeleting(true);
                await onDelete();
                setDeleting(false);
                onRequestClose();
              }}
            />
          </Row>
        </Column>
      )}
    </Popover>
  );
};
